import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { AppShell } from "@/components/AppShell";
import { DotRail } from "@/components/DotRail";
import { MenuHeading } from "@/components/MenuHeading";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/components/AuthProvider";
import { useToast } from "@/hooks/use-toast";
import { useSession, hasProAccess, tierLabel } from "@/components/SessionProvider";
import { usePrices, formatPlanLine } from "@/hooks/usePrices";
import {
  CheckCircle2, XCircle, Loader2, ExternalLink, Crown,
  CreditCard, ShieldCheck, RotateCw, Lock,
  CalendarDays, Sparkles,
} from "lucide-react";
import { ProCompareTable } from "@/components/ProCompareTable";
import { LegalReader } from "@/components/LegalReader";
import { isNative, getPlatform } from "@/lib/platform";
import { restoreCompleteDescription } from "@/lib/iap";
import { billingGrantOf, grantSourceLabel, grantUntilLabel } from "@/lib/billingGrant";

// Apple/Google billing return paths handled by StoreKit/Play Billing on device.
// We surface a confirmation banner, the current subscription state, the account
// standing (plan, member-since, renewal) and a highlight of the Pro feature set
// — unlocked when the trainer is on Pro, previewed as "what you'll unlock" on
// Basic. "Manage subscription" deep-links to the App Store or Google Play
// subscriptions screen.
//
// All copy is intentionally restrained, no em dashes per project style.

// Format an account createdAt (ISO string or epoch ms) as a friendly date.
// Returns null when the value is missing or unparseable so callers can hide the
// row rather than render "Invalid Date".
function formatJoinedDate(value: string | number | null | undefined): string | null {
  if (value == null) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

export default function SettingsBilling() {
  // Two tabs (owner ask, 2026-10-07, after App Review 3.1.2(c)): "Your plan"
  // leads with the current plan and the legal links right under it; "Why go
  // Pro" holds the comparison. The links open the bundled legal pack.
  const [tab, setTab] = useState<"plan" | "why">("plan");
  const [legalTab, setLegalTab] = useState<null | "tos" | "privacy">(null);
  const { authedFetch, account } = useAuth();
  const { toast } = useToast();
  const {
    tier, openPaywall,
    entitlements, subscription, restorePurchases, refreshEntitlements,
  } = useSession();
  const { prices } = usePrices();
  const [restoring, setRestoring] = useState(false);

  const [status, setStatus] = useState<any>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  // Read return query for QA-mode checkout returns.
  const checkoutOutcome = useMemo(() => {
    if (typeof window === "undefined") return null;
    // wouter hash router strips the query at the router level, so peek at the
    // raw hash. Format: #/settings/billing?checkout=success&session_id=...
    const hash = window.location.hash;
    const idx = hash.indexOf("?");
    if (idx === -1) return null;
    const params = new URLSearchParams(hash.slice(idx + 1));
    const c = params.get("checkout");
    if (c === "success" || c === "cancelled") return c;
    return null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch("/api/billing/status");
        const data = await res.json().catch(() => ({}));
        if (!cancelled) setStatus(data);
      } catch {
        // Swallow; non-fatal, we just won't render server state.
      } finally {
        if (!cancelled) setStatusLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedFetch]);

  async function handleRestore() {
    if (restoring) return;
    setRestoring(true);
    try {
      await restorePurchases();
      await refreshEntitlements();
      toast({
        title: "Restore complete",
        description: restoreCompleteDescription(getPlatform()),
      });
    } finally {
      setRestoring(false);
    }
  }

  // B11 (v1.0.10-b2): on iOS, "Manage Subscription" deep links to the
  // system Subscriptions screen. Android uses Play Console's manage URL.
  function openManageSubscription() {
    if (typeof window === "undefined") return;
    // Apple's documented deep link, opens the Subscriptions list in
    // the App Store app.
    window.location.href = "itms-apps://apps.apple.com/account/subscriptions";
  }

  async function openPortal() {
    setPortalLoading(true);
    try {
      const res = await authedFetch("/api/billing/portal", { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.url) {
        toast({
          title: "Portal unavailable",
          description: data?.message || "Could not open the billing portal.",
          variant: "destructive",
        });
        setPortalLoading(false);
        return;
      }
      window.location.href = data.url as string;
    } catch (err: any) {
      toast({
        title: "Network error",
        description: err?.message || "Could not reach billing.",
        variant: "destructive",
      });
      setPortalLoading(false);
    }
  }

  const sub = status?.subscription ?? null;
  const isActive = sub && (sub.status === "active" || sub.status === "trialing");

  // Whether the account currently has the Pro feature set (paid Pro or trial).
  // Drives the "unlocked" vs "will unlock" framing of the feature highlight.
  const isPro = entitlements.pro || hasProAccess(tier);

  // Prefer the server plan code label; fall back to the local tier label so the
  // card always names a plan even before /api/billing/status resolves.
  const planLabel =
    sub?.planCode === "pro_yearly"
      ? "Pro Yearly"
      : sub?.planCode === "pro_monthly"
        ? "Pro Monthly"
        : sub?.planCode === "ultimate_monthly"
          ? "Ultimate"
          : isPro
            ? "Pro"
            : tierLabel(tier);

  const joinedDate = formatJoinedDate(account?.createdAt);
  // Renewal / expiry: the server currentPeriodEnd wins (authoritative), then
  // the mirrored IAP subscription expiry. Guard against an unparseable server
  // date so a bad value hides the row instead of rendering "Invalid Date".
  const serverRenewMs = sub?.currentPeriodEnd ? new Date(sub.currentPeriodEnd).getTime() : NaN;
  const renewMs: number | null = Number.isFinite(serverRenewMs)
    ? serverRenewMs
    : (subscription.expiresAt ?? null);
  const renewDate = renewMs ? new Date(renewMs).toLocaleDateString() : null;
  const isTrial = tier === "trial" || tier === "ultimate_trial" || sub?.status === "trialing";
  // A Pro that is on us (2026-09-16): a referral week, the Android beta
  // thank-you, or a gift. It is said so, with the reason and the end date,
  // and the store buttons that would manage nothing are not offered.
  const grant = isPro ? billingGrantOf(status) : null;

  // The store this build sells through, for the checkout card and the
  // billing line: Apple / App Store on iOS, Google / Google Play on Android,
  // neither on the web (no purchase flow there).
  const platform = getPlatform();
  const storeName = platform === "ios" ? "Apple" : platform === "android" ? "Google" : null;
  const storeLabel = platform === "ios" ? "App Store" : platform === "android" ? "Google Play" : null;

  return (
    <AppShell pageSpacing>
      <div className="max-w-2xl mx-auto pb-24">
        <DotRail>
          {/* b19-ui-cosmetics: adopt the canonical single pt-12 hero wrapper used
              by Profile / Friends / Trade / Pokedex (PR #271), dropping the
              stacked page-top-pad + back-chip spacer so the eyebrow pill lines up
              pixel-for-pixel with every other top-level menu. */}
          <div className="pt-12 page-hero-top" data-dotrail-skip>
            <MenuHeading
              eyebrow="Nexus Dex Pro"
              title="Basic is free."
              greenSuffix="Pro is everything."
              subtitle="Your plan first, then what Pro changes, side by side."
              data-dotrail-skip
            />
          </div>

          {checkoutOutcome === "success" && (
          <div
            data-testid="banner-checkout-success"
            data-dotrail-skip
            className="mb-4 rounded-2xl p-4 flex items-start gap-3"
            style={{
              background: "hsl(150 60% 45% / 0.12)",
              border: "1px solid hsl(150 60% 45% / 0.4)",
            }}
          >
            <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "hsl(150 60% 45%)" }} />
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-tight">Welcome to Pro</p>
              <p className="text-[12px] text-muted-foreground mt-0.5 leading-snug">
                Your Pro subscription is active. We'll send a receipt by email.
                Cancel any time below.
              </p>
            </div>
          </div>
        )}
          {checkoutOutcome === "cancelled" && (
          <div
            data-testid="banner-checkout-cancelled"
            data-dotrail-skip
            className="mb-4 rounded-2xl p-4 flex items-start gap-3"
            style={{
              background: "var(--surface-elev-1)",
              border: "1px solid var(--surface-elev-border)",
            }}
          >
            <XCircle className="w-5 h-5 shrink-0 mt-0.5 text-muted-foreground" />
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-tight">Checkout cancelled</p>
              <p className="text-[12px] text-muted-foreground mt-0.5 leading-snug">
                No charge was made. You can try again whenever you're ready.
              </p>
            </div>
          </div>
        )}

          <div
            role="tablist"
            aria-label="Plan and Pro"
            className="flex gap-1 rounded-full p-1 mb-4"
            style={{ background: "var(--surface-sunken)", border: "1px solid var(--border-subtle)" }}
            data-dotrail-skip
            data-testid="billing-tabs"
          >
            {(["plan", "why"] as const).map((t) => {
              const on = tab === t;
              return (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => setTab(t)}
                  data-testid={`billing-tab-${t}`}
                  className="flex-1 rounded-full py-2 text-[12px] font-bold transition-colors"
                  style={
                    on
                      ? { background: "var(--accent-fill)", color: "var(--text-on-accent)" }
                      : { color: "var(--text-secondary)" }
                  }
                >
                  {t === "plan" ? "Your plan" : "Why go Pro"}
                </button>
              );
            })}
          </div>

          {/* ── Pro feature highlight (the "Why go Pro" tab) ──────────────
              Pro: a proud "unlocked" list. Basic: the same list framed as what
              upgrading turns on, with an inline upgrade CTA + price. */}
          {tab === "why" && (
          <div
            data-dotrail-dot
            className="rounded-3xl p-5"
            style={{
              background: "var(--surface-elev-1)",
              border: isPro
                ? "1px solid hsl(var(--primary) / 0.28)"
                : "1px solid var(--surface-elev-border)",
            }}
            data-testid="card-pro-features"
          >
            <div className="flex items-center gap-2 mb-1">
              {isPro
                ? <Sparkles className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} />
                : <Crown className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} />}
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
                {isPro ? "Included with Pro" : "Unlock with Pro"}
              </p>
            </div>
            <h3 className="text-lg font-bold tracking-tight">
              {isPro ? "Your perks, unlocked." : "Basic and Pro, side by side."}
            </h3>
            <p className="text-[12px] text-muted-foreground mt-0.5 leading-snug">
              {isPro
                ? "Everything below is live on your account right now."
                : "Basic has plenty to explore. This is what upgrading turns on."}
            </p>

            <ProCompareTable testIdPrefix="billing-pro" className="mt-4" />
            {!isPro && (
              <div className="mt-4 grid gap-2">
                <Button
                  data-testid="button-upgrade-why"
                  onClick={() => openPaywall("settings-billing", "Upgrade to Pro")}
                  className="rounded-full font-semibold"
                  style={{ background: "linear-gradient(135deg, #00E676, #00B248)", color: "white" }}
                >
                  <Crown className="w-4 h-4 mr-2" /> Upgrade to Pro
                </Button>
                <p className="text-center text-[11px] text-muted-foreground">
                  {formatPlanLine(prices.monthly, "/mo")} or {formatPlanLine(prices.annual, "/yr")}. Cancel any time.
                </p>
              </div>
            )}
            <BillingLegalLinks storeLabel={storeLabel} onOpen={setLegalTab} testIdPrefix="billing-why" />
          </div>
          )}

          {/* ── Current plan hero (the "Your plan" tab) ───────────────────
              Names the plan, badges the standing (Pro / Trial / Basic), and
              surfaces the account facts the trainer actually asks for: when
              they joined and when the plan renews. */}
          {tab === "plan" && (<>
          <div
            data-dotrail-dot
            className="mt-4 rounded-3xl p-5 relative overflow-hidden"
            style={{
              background: isPro
                ? "linear-gradient(150deg, hsl(var(--primary) / 0.16), var(--surface-elev-1) 62%)"
                : "var(--surface-elev-1)",
              border: isPro
                ? "1px solid hsl(var(--primary) / 0.4)"
                : "1px solid var(--surface-elev-border)",
            }}
          >
            {/* Soft accent bloom, Pro only. */}
            {isPro && (
              <div
                aria-hidden
                className="absolute -top-10 -right-8 w-40 h-40 rounded-full pointer-events-none"
                style={{ background: "hsl(var(--primary) / 0.14)", filter: "blur(8px)" }}
              />
            )}

            <div className="flex items-center gap-2 mb-1 relative">
              <Crown className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} />
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
                Current plan
              </p>
            </div>

            {statusLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground py-2 relative">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Loading...</span>
              </div>
            ) : (
              <div className="relative">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <p className="text-2xl font-bold tracking-tight" data-testid="text-plan-label">
                    {planLabel}
                  </p>
                  <span
                    className="text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full"
                    style={
                      isPro
                        ? { background: "hsl(var(--primary) / 0.16)", color: "hsl(var(--primary))", border: "1px solid hsl(var(--primary) / 0.4)" }
                        : { background: "var(--surface-elev-2)", color: "hsl(var(--muted-foreground))", border: "1px solid var(--surface-elev-border)" }
                    }
                    data-testid="badge-plan-standing"
                  >
                    {grant ? "On us" : isTrial ? "Trial" : isPro ? "Active" : "Free"}
                  </span>
                </div>

                {/* Account facts */}
                <dl className="mt-4 space-y-2">
                  {joinedDate && (
                    <div className="flex items-center justify-between gap-2 text-[12px]">
                      <dt className="text-muted-foreground inline-flex items-center gap-1.5">
                        <CalendarDays className="w-3.5 h-3.5" /> Member since
                      </dt>
                      <dd className="font-semibold" data-testid="text-member-since">{joinedDate}</dd>
                    </div>
                  )}
                  {grant && (
                    <>
                      <div className="flex items-center justify-between gap-2 text-[12px]">
                        <dt className="text-muted-foreground">Why</dt>
                        <dd className="font-semibold" data-testid="text-grant-source">{grantSourceLabel(grant.source)}</dd>
                      </div>
                      <div className="flex items-center justify-between gap-2 text-[12px]">
                        <dt className="text-muted-foreground">Cost</dt>
                        <dd className="font-semibold" data-testid="text-grant-until">{grantUntilLabel(grant)}</dd>
                      </div>
                    </>
                  )}
                  {!grant && sub?.status && (
                    <div className="flex items-center justify-between gap-2 text-[12px]">
                      <dt className="text-muted-foreground">Status</dt>
                      <dd className="font-semibold capitalize" data-testid="text-sub-status">{sub.status}</dd>
                    </div>
                  )}
                  {!grant && isPro && renewDate && (
                    <div className="flex items-center justify-between gap-2 text-[12px]">
                      <dt className="text-muted-foreground">{isTrial ? "First charge" : "Renews"}</dt>
                      <dd className="font-semibold" data-testid="text-renew-date">{renewDate}</dd>
                    </div>
                  )}
                  {account?.email && (
                    <div className="flex items-center justify-between gap-2 text-[12px] min-w-0">
                      <dt className="text-muted-foreground shrink-0">Account</dt>
                      <dd className="font-semibold font-mono truncate ml-3" title={account.email}>
                        {account.email}
                      </dd>
                    </div>
                  )}
                </dl>

                <div className="mt-5 grid gap-2">
                  {isActive ? (
                    <Button
                      data-testid="button-manage-billing"
                      onClick={openPortal}
                      disabled={portalLoading}
                      className="rounded-full"
                    >
                      {portalLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" /> Manage billing
                          <ExternalLink className="w-3.5 h-3.5 ml-2 opacity-70" />
                        </>
                      )}
                    </Button>
                  ) : !isPro ? (
                    <Button
                      data-testid="button-upgrade"
                      onClick={() => openPaywall("settings-billing", "Upgrade to Pro")}
                      className="rounded-full font-semibold"
                      style={{ background: "linear-gradient(135deg, #00E676, #00B248)", color: "white" }}
                    >
                      <Crown className="w-4 h-4 mr-2" /> Upgrade to Pro
                    </Button>
                  ) : null}
                  {!isPro && (
                    <p className="text-center text-[11px] text-muted-foreground" data-testid="text-plan-prices">
                      {formatPlanLine(prices.monthly, "/mo")} or {formatPlanLine(prices.annual, "/yr")}. Cancel any time.
                    </p>
                  )}
                  {grant && (
                    <p className="text-center text-[11px] text-muted-foreground leading-snug" data-testid="text-grant-note">
                      Nothing to pay and nothing to cancel.
                      {grant.expiresAt != null
                        ? ` When it ends you go back to Basic unless you subscribe: ${formatPlanLine(prices.monthly, "/mo")} or ${formatPlanLine(prices.annual, "/yr")}.`
                        : ""}
                    </p>
                  )}
                  {grant && grant.expiresAt != null && getPlatform() === "ios" && (
                    <Button
                      data-testid="button-keep-pro"
                      onClick={() => openPaywall("settings-billing", "Keep Pro after your free period")}
                      variant="secondary"
                      className="rounded-full font-semibold"
                    >
                      <Crown className="w-4 h-4 mr-2" /> Keep Pro after that
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Terms and privacy, right under the plan (App Review 3.1.2(c),
              2026-10-07): the subscription's name, period and price sit above,
              the renewal terms and both legal links here. */}
          <div
            data-dotrail-dot
            className="mt-4 rounded-2xl p-4"
            style={{ background: "var(--surface-elev-1)", border: "1px solid var(--surface-elev-border)" }}
            data-testid="card-billing-legal"
          >
            <BillingLegalLinks storeLabel={storeLabel} onOpen={setLegalTab} testIdPrefix="billing-plan" />
          </div>

          {/* ── Subscription controls ─────────────────────────────────────
              Manage (native Pro) + restore purchases. Source of truth is the
              entitlements field on SessionProvider which mirrors the server. */}
          <div
            data-dotrail-dot
            className="mt-4 rounded-2xl p-4"
            style={{
              background: "var(--surface-elev-1)",
              border: "1px solid var(--surface-elev-border)",
            }}
            data-testid="card-subscription-detail"
          >
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} />
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
                Subscription
              </p>
            </div>
            <div className="flex items-center justify-between text-[12px]">
              <span className="text-muted-foreground">Tier</span>
              <span className="font-semibold" data-testid="text-tier">
                {entitlements.pro ? (grant ? "Pro, on us" : "Pro") : "Basic"}
              </span>
            </div>
            <div className="mt-4 grid gap-2">
              {entitlements.pro && isNative() && !grant && (
                <Button
                  data-testid="button-manage-subscription"
                  onClick={openManageSubscription}
                  variant="secondary"
                  className="rounded-full"
                >
                  Manage subscription
                  <ExternalLink className="w-3.5 h-3.5 ml-2 opacity-70" />
                </Button>
              )}
              <Button
                data-testid="button-restore-purchases"
                onClick={handleRestore}
                disabled={restoring}
                variant="ghost"
                className="rounded-full"
              >
                {restoring ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Restoring
                  </>
                ) : (
                  <>
                    <RotateCw className="w-4 h-4 mr-2" /> Restore purchases
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* The Storage Maximisation tile lived here until the page was deleted (2026-08-21). */}

          <div
            data-dotrail-dot
            className="mt-4 rounded-2xl p-4 flex items-start gap-3"
            style={{
              background: "var(--surface-elev-1)",
              border: "1px solid var(--surface-elev-border)",
            }}
          >
            <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "hsl(var(--teal))" }} />
            <div className="min-w-0">
              {/* Only the store THIS build sells through is named (App Review
                  2026-10-07, guideline 2.3.10: an iOS binary must not point at
                  Android or Google Play). The web has no purchase flow and
                  says so without naming either. */}
              <p className="text-sm font-semibold leading-tight">
                {storeName ? `Secure checkout by ${storeName}` : "Secure checkout"}
              </p>
              <p className="text-[12px] text-muted-foreground mt-0.5 leading-snug">
                {storeName
                  ? `Pro is sold through the ${storeLabel}. Manage receipts, refunds and cancellations from your ${storeName} account.`
                  : "Pro is sold through the app's store checkout. Manage receipts, refunds and cancellations from your store account."}
              </p>
            </div>
          </div>

          <p className="mt-3 text-[10px] text-muted-foreground/70 text-center" data-dotrail-skip>
            {storeLabel ? `billing: in-app purchase via the ${storeLabel}` : "billing: in-app purchase"}
          </p>
          </>)}

          <div className="mt-10" data-dotrail-skip />
        </DotRail>
      </div>
      <LegalReader open={legalTab !== null} initialTab={legalTab ?? "tos"} onClose={() => setLegalTab(null)} />
    </AppShell>
  );
}

/**
 * The auto-renewal terms and the two legal links Apple wants wherever a
 * subscription is described (guideline 3.1.2(c)). Drawn under the plan on
 * the "Your plan" tab and under the comparison on "Why go Pro"; the links
 * open the bundled legal pack (the same Terms and Privacy as Settings).
 */
function BillingLegalLinks({
  storeLabel,
  onOpen,
  testIdPrefix,
}: {
  storeLabel: string | null;
  onOpen: (tab: "tos" | "privacy") => void;
  testIdPrefix: string;
}) {
  return (
    <div data-testid={`${testIdPrefix}-legal`}>
      <p className="text-[10.5px] leading-relaxed text-muted-foreground">
        Nexus Dex Pro is an auto-renewable subscription, monthly or yearly. Payment is charged to your{" "}
        {storeLabel ?? "app store"} account at confirmation of purchase and renews automatically for the same
        price and period unless cancelled at least 24 hours before the period ends.
      </p>
      <div className="mt-2 flex items-center gap-2 text-[12px] font-semibold">
        <button
          type="button"
          onClick={() => onOpen("tos")}
          className="underline underline-offset-2"
          style={{ color: "var(--accent-fill)" }}
          data-testid={`${testIdPrefix}-terms`}
        >
          Terms of Use (EULA)
        </button>
        <span aria-hidden="true" className="text-muted-foreground">·</span>
        <button
          type="button"
          onClick={() => onOpen("privacy")}
          className="underline underline-offset-2"
          style={{ color: "var(--accent-fill)" }}
          data-testid={`${testIdPrefix}-privacy`}
        >
          Privacy Policy
        </button>
      </div>
    </div>
  );
}
